import { z } from 'zod';

import { INDEX_CODES, IndexCodeSchema, SectorIdSchema, WINDOW_KEYS, type WindowKey } from './enums';
import { TickerSchema } from './primitives';

const NullableNumberSchema = z.number().nullable();

/** Per-window map: every WINDOW_KEYS entry present; value may be null (FR-F2). */
export const WindowNumberMapSchema = z.object(
  Object.fromEntries(WINDOW_KEYS.map((k) => [k, NullableNumberSchema])) as Record<
    WindowKey,
    typeof NullableNumberSchema
  >,
);

export const SecurityFundamentalsSchema = z.object({
  /** Market capitalisation. Missing is null, never 0 (plan §6.3 · R2b). */
  mcap: z.number().nonnegative().nullable(),
  /** Price/earnings ratio. Missing is null, never 0 (plan R2b / traps). */
  pe: NullableNumberSchema,
  /** Dividend yield — NOT diversification (see DIVERSIFICATION_KINDS). */
  div: NullableNumberSchema,
  /** Revenue. Missing is null, never 0 (plan §6.3 · R2b). */
  rev: z.number().nonnegative().nullable(),
});
export type SecurityFundamentals = z.infer<typeof SecurityFundamentalsSchema>;

/**
 * Composition source for `SecuritySchema`, not a wire shape of its own —
 * `Security` flattens `ret`/`dd` into itself, so no payload is ever a
 * bare `SecurityWindowMetrics`.
 */
export const SecurityWindowMetricsSchema = z.object({
  /** Total return per window. */
  ret: WindowNumberMapSchema,
  /** Max drawdown per window (negative). */
  dd: WindowNumberMapSchema,
});
export type SecurityWindowMetrics = z.infer<typeof SecurityWindowMetricsSchema>;

/* JSON `null` on a synthetic-only optional is treated as absent. */
const NullAsAbsentNumberSchema = z.preprocess(
  (v) => (v === null ? undefined : v),
  z.number().optional(),
);
const NullAsAbsentBoolSchema = z.preprocess(
  (v) => (v === null ? undefined : v),
  z.boolean().optional(),
);
const NullAsAbsentFactorsSchema = z.preprocess(
  (v) => (v === null ? undefined : v),
  z.array(z.number()).length(5).optional(),
);

/**
 * Security row shared by synthetic fixtures, local repository, and live API.
 * Factor loadings (`f`, `nrm`) are synthetic-only and optional on the wire.
 * Composed from fundamentals + window metrics so nullability cannot drift.
 */
export const SecuritySchema = SecurityFundamentalsSchema.extend(
  SecurityWindowMetricsSchema.shape,
).extend({
  ticker: TickerSchema,
  name: z.string().min(1).max(200),
  /** Sector code — same value as `Sector.id`; `sector` below is the display name. */
  sc: SectorIdSchema,
  sector: z.string().min(1).max(100),
  price: z.number().nonnegative(),
  /**
   * Annualised volatility — not volume. NULLABLE by ADR-0022: below §6.5b's
   * minimum aligned returns for the active window, `security_window_metrics`
   * carries a row whose four metric values are all `null`, and this is the wire
   * projection of one of them. A volatility of `0` is a real observation — a
   * security that did not move — so coalescing "unknown" into it is the R2b trap
   * applied to a metric instead of a fundamental.
   */
  vol: z.number().nonnegative().nullable(),
  /** Beta vs SPX. Nullable for the same §6.5b reason as `vol` (ADR-0022). */
  beta: z.number().nullable(),
  /**
   * Index MEMBERSHIPS. Distinct from the `idx` URL param, which is a filter.
   * Distinctness sits on the FIELD, not the object: a `superRefine` on
   * `SecuritySchema` would wrap it and cost every consumer `.extend()`,
   * `.pick()` and `.partial()`.
   */
  idx: z
    .array(IndexCodeSchema)
    .min(1)
    .max(INDEX_CODES.length)
    .refine((v) => new Set(v).size === v.length, {
      message: 'index memberships must be distinct',
    }),
  /** Factor loadings, synthetic-only. */
  f: NullAsAbsentFactorsSchema,
  /** Normalised composite risk input, synthetic-only. */
  nrm: NullAsAbsentNumberSchema,
  insufficientHistory: NullAsAbsentBoolSchema,
});
export type Security = z.infer<typeof SecuritySchema>;
